import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { sendLoginOTP, loginWithOTP } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

/**
 * ResetPage
 *
 * Since your auth is OTP-based (passwordless), "reset" means:
 * Step 1 → enter email
 * Step 2 → enter OTP sent to that email
 * Step 3 → success, auto-login (no new password needed)
 *
 * If your app ever adds a password layer, Step 3 becomes a
 * "set new password" form — the structure is already there.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState("email");       // email | otp | success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const emailRef = useRef(null);

  // ── Timer ─────────────────────────────────────────────────
  useEffect(() => {
    if (step !== "otp") return;
    if (timer === 0) { setCanResend(true); return; }
    const id = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(id);
  }, [step, timer]);

  useEffect(() => {
    if (step === "email") setTimeout(() => emailRef.current?.focus(), 50);
    if (step === "otp") setTimeout(() => document.getElementById("reset-otp-0")?.focus(), 100);
  }, [step]);

  // ── Send OTP ──────────────────────────────────────────────
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email.trim()) return setError("Enter your email address");
    setLoading(true);
    setError("");
    try {
      await sendLoginOTP(email.trim().toLowerCase());
      setStep("otp");
      setTimer(60);
      setCanResend(false);
      setOtp(["", "", "", "", "", ""]);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send code. Check your email.");
    } finally {
      setLoading(false);
    }
  };

  // ── Verify OTP → auto login ───────────────────────────────
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) return setError("Enter all 6 digits");
    setLoading(true);
    setError("");
    try {
      const data = await loginWithOTP(email, code);
      login(data.token);
      setStep("success");
      setTimeout(() => navigate("/home", { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid code — try again");
      setOtp(["", "", "", "", "", ""]);
      setTimeout(() => document.getElementById("reset-otp-0")?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  // ── Resend ────────────────────────────────────────────────
  const handleResend = async () => {
    if (!canResend) return;
    setOtp(["", "", "", "", "", ""]);
    setTimer(60);
    setCanResend(false);
    setError("");
    try {
      await sendLoginOTP(email);
      toast.success("New code sent!");
      setTimeout(() => document.getElementById("reset-otp-0")?.focus(), 50);
    } catch { setError("Failed to resend code"); }
  };

  // ── OTP handlers ──────────────────────────────────────────
  const handleOtpChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const arr = [...otp]; arr[i] = val; setOtp(arr); setError("");
    if (val && i < 5) document.getElementById(`reset-otp-${i + 1}`)?.focus();
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && !otp[i] && i > 0)
      document.getElementById(`reset-otp-${i - 1}`)?.focus();
  };
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6).split("");
    const arr = [...otp];
    digits.forEach((d, i) => { arr[i] = d; });
    setOtp(arr);
    setTimeout(() => document.getElementById(`reset-otp-${Math.min(digits.length - 1, 5)}`)?.focus(), 10);
  };

  const otpComplete = otp.every(d => d !== "");

  // ── Step indicator ────────────────────────────────────────
  const steps = [
    { key: "email", label: "Email" },
    { key: "otp", label: "Verify" },
    { key: "success", label: "Done" },
  ];
  const currentStepIndex = steps.findIndex(s => s.key === step);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: "linear-gradient(160deg, #f0fdf4 0%, #ecfdf5 40%, #f0fdfa 100%)", fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-25"
          style={{ background: "radial-gradient(circle, #6ee7b7, transparent)" }} />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-15"
          style={{ background: "radial-gradient(circle, #34d399, transparent)" }} />
      </div>

      <div className="w-full max-w-[400px] relative z-10">

        {/* Back button */}
        {step !== "success" && (
          <button
            onClick={() => step === "otp" ? setStep("email") : navigate("/auth")}
            className="flex items-center gap-2 text-slate-500 text-sm font-medium mb-6 hover:text-slate-700 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            {step === "otp" ? "Change email" : "Back to sign in"}
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg"
            style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 8px 32px rgba(16,185,129,0.35)" }}>
            <i className={`text-white text-2xl ${
              step === "success" ? "ri-checkbox-circle-line" :
              step === "otp" ? "ri-mail-check-line" : "ri-lock-unlock-line"
            }`} />
          </div>
          <h1 className="text-slate-800 text-2xl font-bold tracking-tight">
            {step === "email" && "Reset access"}
            {step === "otp" && "Enter the code"}
            {step === "success" && "You're back in!"}
          </h1>
          <p className="text-slate-500 text-sm mt-1.5">
            {step === "email" && "We'll send a verification code to your email"}
            {step === "otp" && <>Code sent to <span className="font-semibold text-slate-700">{email}</span></>}
            {step === "success" && "Redirecting you to your account…"}
          </p>
        </div>

        {/* Step progress */}
        {step !== "success" && (
          <div className="flex items-center justify-center gap-2 mb-7">
            {steps.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                  i < currentStepIndex
                    ? "bg-emerald-500 text-white"
                    : i === currentStepIndex
                    ? "bg-emerald-600 text-white ring-4 ring-emerald-100"
                    : "bg-slate-100 text-slate-400"
                }`}>
                  {i < currentStepIndex ? <i className="ri-check-line text-xs" /> : i + 1}
                </div>
                <span className={`text-xs font-medium ${
                  i <= currentStepIndex ? "text-slate-600" : "text-slate-300"
                }`}>{s.label}</span>
                {i < steps.length - 1 && (
                  <div className={`w-8 h-px ${i < currentStepIndex ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div className="bg-white rounded-3xl overflow-hidden"
          style={{ boxShadow: "0 4px 6px rgba(0,0,0,0.04), 0 20px 60px rgba(16,185,129,0.1), 0 0 0 1px rgba(0,0,0,0.05)" }}>
          <div className="p-7">

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-2xl mb-5 bg-red-50 border border-red-100">
                <i className="ri-error-warning-line text-red-500 flex-shrink-0" />
                <span className="text-red-600 text-sm font-medium">{error}</span>
              </div>
            )}

            {/* ── STEP: EMAIL ── */}
            {step === "email" && (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-base" />
                  <input
                    ref={emailRef}
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(""); }}
                    placeholder="your@email.com"
                    required
                    className="w-full pl-10 pr-4 py-3.5 rounded-2xl text-sm text-slate-800 placeholder-slate-400 bg-slate-50 border border-slate-200 outline-none transition-all focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-50"
                  />
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 flex items-start gap-2.5">
                  <i className="ri-information-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
                  <p className="text-amber-700 text-xs leading-relaxed">
                    Since your account uses passwordless login, we'll send you a one-time code to verify it's you.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 4px 16px rgba(16,185,129,0.35)" }}
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Sending code…</>
                  ) : (
                    <>Send verification code <i className="ri-arrow-right-line" /></>
                  )}
                </button>
              </form>
            )}

            {/* ── STEP: OTP ── */}
            {step === "otp" && (
              <form onSubmit={handleVerifyOTP} className="space-y-5">
                <div className="flex justify-center gap-2.5">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      id={`reset-otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(i, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(i, e)}
                      onPaste={handleOtpPaste}
                      className="text-center text-xl font-bold rounded-2xl outline-none transition-all border-2"
                      style={{
                        width: "48px",
                        height: "56px",
                        background: digit ? "#f0fdf4" : "#f8fafc",
                        borderColor: digit ? "#10b981" : "#e2e8f0",
                        color: digit ? "#059669" : "#1e293b",
                        boxShadow: digit ? "0 0 0 4px rgba(16,185,129,0.1)" : "none",
                      }}
                    />
                  ))}
                </div>

                <div className="text-center">
                  {canResend ? (
                    <button type="button" onClick={handleResend}
                      className="text-emerald-600 text-sm font-semibold hover:text-emerald-700 transition-colors">
                      Resend code
                    </button>
                  ) : (
                    <p className="text-slate-400 text-sm">
                      Resend in <span className="font-semibold text-slate-600 tabular-nums">{timer}s</span>
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !otpComplete}
                  className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 4px 16px rgba(16,185,129,0.35)" }}
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Verifying…</>
                  ) : (
                    <><i className="ri-shield-check-line" />Verify & Sign In</>
                  )}
                </button>
              </form>
            )}

            {/* ── STEP: SUCCESS ── */}
            {step === "success" && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl" />
                </div>
                <p className="text-slate-800 font-bold text-base mb-1">Identity verified!</p>
                <p className="text-slate-400 text-sm">Taking you to your account…</p>
                <div className="mt-4 flex justify-center">
                  <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-slate-400 text-xs mt-5">
          Remember your details?{" "}
          <button onClick={() => navigate("/auth")}
            className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
